import React, { useCallback, useEffect, useState } from "react";
import baseUrl from "../../../api/baseUrl";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import CloseIcon from "@mui/icons-material/Close";
import AddIcon from "@mui/icons-material/Add";
import MedicationIcon from "@mui/icons-material/Medication";
import { toast } from "react-toastify";
import InputDropSearch from "../../../components/inputs/InputDropSearch";
import { normalizeDoseList } from "../../manage/prescription/prescriptionDispenseUtils";
import { isInventoryEnabled } from "../../../utils/clinicFeatures";
import { scales, scaleLabel, strengthUnits } from "../../../data/scale/scale";

/** One inventory product (brand) that can fill a dose. */
type LinkedProduct = {
  productId: string;
  productName: string;
  availableQuantity?: number;
  scale?: string;
};

type DoseRow = {
  _id?: string;
  value: string;
  /** A dose may be filled by several brands. */
  products: LinkedProduct[];
};

type FormDataType = {
  drugs: string;
  dose: string;
  doses: DoseRow[];
  /** Strength unit printed after the dose ("500mg"). */
  type: string;
  /** Stock unit (same list as product scale); linked products must match. */
  form: string;
};

const emptyDose = (): DoseRow => ({ value: "", products: [] });

const INITIAL_FORM_DATA: FormDataType = {
  drugs: "",
  dose: "",
  doses: [emptyDose()],
  type: "",
  form: "",
};

function AddDrugs({ close, id }: any) {
  // Linking doses to stock only makes sense with the Inventory module on
  const inventoryEnabled = isInventoryEnabled();
  const [formData, setformData] = useState(INITIAL_FORM_DATA);
  const [products, setProducts] = useState<any[]>([]);

  const fetchProducts = useCallback(async (keyword = "") => {
    try {
      const response = await baseUrl.get(
        "/client/prescription/prescription/searchProductsForDrugLink",
        { headers: { keyword } }
      );
      setProducts(response.data.data || []);
    } catch (error) {
      console.error(error);
    }
  }, []);

  useEffect(() => {
    if (!inventoryEnabled) return;
    fetchProducts("");
  }, [fetchProducts, inventoryEnabled]);

  const onchange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;

    if (name.startsWith("doses/")) {
      const index = Number(name.split("/")[1]);
      setformData((prevState) => ({
        ...prevState,
        doses: prevState.doses.map((dose, i) =>
          i === index ? { ...dose, value } : dose
        ),
      }));
    } else {
      setformData((prevState) => ({
        ...prevState,
        [name]: value,
      }));
    }
  };

  useEffect(() => {
    if (id) {
      const loaded = normalizeDoseList(id.dose);
      const doses: DoseRow[] =
        loaded.length > 0
          ? loaded.map((d) => ({
              _id: d._id,
              value: d.value,
              products: [],
            }))
          : [emptyDose()];

      setformData((prev) => ({
        ...prev,
        doses,
        drugs: id.drugs ?? prev.drugs,
        type: id.type ?? prev.type,
        form: id.form ?? "",
      }));

      // Resolve currently linked products for each dose
      if (!inventoryEnabled) return;
      baseUrl
        .get("/client/prescription/prescription/searchProductsForDrugLink")
        .then((response) => {
          const list: any[] = response.data.data || [];
          setProducts(list);
          setformData((prev) => ({
            ...prev,
            doses: prev.doses.map((dose) => {
              if (!dose._id) return dose;
              const linked = list.filter(
                (p) => String(p.linkedDrugDoseId || "") === String(dose._id)
              );
              return {
                ...dose,
                products: linked.map((p) => ({
                  productId: p._id,
                  productName: p.productName,
                  availableQuantity: p.availableQuantity,
                  scale: p.scale,
                })),
              };
            }),
          }));
        })
        .catch((error) => console.error(error));
    } else {
      setformData(INITIAL_FORM_DATA);
    }
  }, [id, inventoryEnabled]);

  const add = () => {
    setformData((prevState) => ({
      ...prevState,
      doses: [...(Array.isArray(prevState.doses) ? prevState.doses : []), emptyDose()],
    }));
  };

  const removeDoseByIndex = (indexToRemove: number) => {
    setformData((prevState) => {
      const currentDoses = Array.isArray(prevState.doses) ? prevState.doses : [];
      return {
        ...prevState,
        doses: currentDoses.filter((_, index) => index !== indexToRemove),
      };
    });
  };

  const setDoseProducts = (
    index: number,
    update: (products: LinkedProduct[]) => LinkedProduct[]
  ) => {
    setformData((prev) => ({
      ...prev,
      doses: prev.doses.map((d, i) =>
        i === index ? { ...d, products: update(d.products || []) } : d
      ),
    }));
  };

  /** Link one more brand to this dose. Several may fill the same dose. */
  const handleAddProduct = async (
    index: number,
    e: { target: { name: string; value: any; valueTitle: string; value2: any } }
  ) => {
    const productId = e.target.value;
    if (!productId) return;

    const dose = formData.doses[index];
    if (
      (dose.products || []).some(
        (p) => String(p.productId) === String(productId)
      )
    ) {
      return;
    }

    // Link only when the catalog dose already has an _id (saved drug)
    if (!id?._id || !dose?._id) {
      toast.info("Save the drug first, then link products to doses.");
      return;
    }

    const picked = e.target.value2;
    const entry: LinkedProduct = {
      productId: String(productId),
      productName: e.target.valueTitle || "",
      availableQuantity: picked?.availableQuantity,
      scale: picked?.scale,
    };
    setDoseProducts(index, (products) => [...products, entry]);

    try {
      const response = await baseUrl.patch(
        "/client/prescription/prescription/linkProductToDose",
        { productId, doseId: dose._id },
        { headers: { prescriptionid: id._id } }
      );
      // A drug without a form takes its first product's unit server-side;
      // mirror it so pressing Update doesn't send the old empty form back.
      const drugForm = response.data?.drugForm;
      if (drugForm) {
        setformData((prev) => (prev.form ? prev : { ...prev, form: drugForm }));
      }
    } catch (error: any) {
      console.error(error);
      toast.error(
        error?.response?.data?.message || "Failed to link the product."
      );
      setDoseProducts(index, (products) =>
        products.filter((p) => String(p.productId) !== String(productId))
      );
    }
  };

  /** Unlink a single brand — never by doseId, which would drop them all. */
  const handleRemoveProduct = async (index: number, productId: string) => {
    const dose = formData.doses[index];
    const removed = (dose.products || []).find(
      (p) => String(p.productId) === String(productId)
    );
    setDoseProducts(index, (products) =>
      products.filter((p) => String(p.productId) !== String(productId))
    );

    if (!id?._id || !dose?._id) return;

    try {
      await baseUrl.patch(
        "/client/prescription/prescription/unlinkProductFromDose",
        { productId },
        { headers: { prescriptionid: id._id } }
      );
    } catch (error: any) {
      console.error(error);
      toast.error(
        error?.response?.data?.message || "Failed to unlink the product."
      );
      if (removed) {
        setDoseProducts(index, (products) => [...products, removed]);
      }
    }
  };

  const onsubmit = (e: any) => {
    e.preventDefault();

    const dosesPayload = (formData.doses || [])
      .map((d) => ({
        ...(d._id ? { _id: d._id } : {}),
        value: String(d.value || "").trim(),
      }))
      .filter((d) => d.value);

    const seen = new Set<string>();
    for (const d of dosesPayload) {
      if (seen.has(d.value)) {
        toast.error(`Duplicate dose value: ${d.value}`);
        return;
      }
      seen.add(d.value);
    }

    if (dosesPayload.length === 0) {
      toast.error("Add at least one dose.");
      return;
    }

    const body = {
      drugs: formData.drugs,
      type: formData.type,
      form: formData.form,
      doses: dosesPayload,
    };

    if (id === "") {
      baseUrl
        .post("/client/prescription/prescription/createPrescription", body)
        .then(() => {
          close(false);
        })
        .catch((error) => {
          console.log(error);
          toast.error(
            error?.response?.data?.message || "Failed to create drug."
          );
        });
    } else {
      baseUrl
        .patch(
          "/client/prescription/prescription/updatePrescriptionById",
          body,
          { headers: { prescriptionid: id?._id } }
        )
        .then(() => {
          close(false);
        })
        .catch((error) => {
          console.log(error);
          toast.error(
            error?.response?.data?.message || "Failed to update drug."
          );
        });
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 bg-gradient-to-r from-emerald-500 to-teal-600 flex items-center justify-between shrink-0">
          <div className="flex items-center gap-3">
            <MedicationIcon className="text-white" sx={{ fontSize: 24 }} />
            <h2 className="text-lg font-bold text-white">
              {id === "" ? "Add Drug" : "Update Drug"}
            </h2>
          </div>
          <button
            onClick={() => close(false)}
            className="w-8 h-8 rounded-lg bg-white/20 hover:bg-white/30 flex items-center justify-center text-white transition-colors"
          >
            <CloseIcon sx={{ fontSize: 20 }} />
          </button>
        </div>

        {/* Form */}
        <div className="p-6 space-y-4 overflow-y-auto">
          {/* Drug Name */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1.5">
              Drug Name
            </label>
            <input
              onChange={onchange}
              value={formData.drugs}
              name="drugs"
              type="text"
              placeholder="Enter drug name"
              className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-transparent outline-none transition-all"
            />
          </div>

          {/* Doses */}
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <label className="block text-sm font-medium text-slate-700">Doses</label>
              <button
                type="button"
                onClick={() => add()}
                className="flex items-center gap-1 px-3 py-1 text-xs font-medium text-emerald-600 bg-emerald-50 rounded-lg hover:bg-emerald-100 transition-colors"
              >
                <AddIcon sx={{ fontSize: 16 }} />
                Add Dose
              </button>
            </div>
            <div className="space-y-3 max-h-72 overflow-y-auto">
              {(Array.isArray(formData.doses) ? formData.doses : []).map(
                (dose, index) => (
                  <div
                    key={dose._id || index}
                    className="rounded-xl border border-slate-100 bg-slate-50/50 p-3 space-y-2"
                  >
                    <div className="flex items-center gap-2">
                      <input
                        value={dose.value}
                        name={`doses/${index}`}
                        onChange={(e) => onchange(e)}
                        placeholder={`Dose ${index + 1}`}
                        className="flex-1 px-4 py-2 bg-white border border-slate-200 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-transparent outline-none transition-all text-sm"
                      />
                      {Array.isArray(formData.doses) &&
                        formData.doses.length > 1 && (
                          <button
                            type="button"
                            onClick={() => removeDoseByIndex(index)}
                            className="p-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                          >
                            <DeleteOutlineIcon sx={{ fontSize: 18 }} />
                          </button>
                        )}
                    </div>
                    {inventoryEnabled && (dose.products || []).length > 0 && (
                      <div className="space-y-1">
                        {(dose.products || []).map((p) => (
                          <div
                            key={p.productId}
                            className="flex items-center justify-between gap-2 rounded-lg bg-white border border-slate-200 px-2.5 py-1.5"
                          >
                            <span className="min-w-0 truncate text-xs text-slate-700">
                              {p.productName}
                              <span className="ml-1.5 text-[11px] text-slate-400">
                                stock {p.availableQuantity ?? "—"}
                                {p.scale ? ` ${p.scale}` : ""}
                              </span>
                            </span>
                            <button
                              type="button"
                              onClick={() =>
                                handleRemoveProduct(index, p.productId)
                              }
                              className="shrink-0 p-1 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-md transition-colors"
                              aria-label={`Unlink ${p.productName}`}
                              title="Unlink this product"
                            >
                              <CloseIcon sx={{ fontSize: 14 }} />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                    {inventoryEnabled && (
                    <InputDropSearch
                      data={
                        formData.form
                          ? products.filter((p) => p.scale === formData.form)
                          : products
                      }
                      name={`product/${index}`}
                      label={
                        (dose.products || []).length > 0
                          ? "Add another product"
                          : "Linked product"
                      }
                      selectName1="productName"
                      selectName2="availableQuantity"
                      noMgs="—"
                      extraMsg=" stock"
                      selectValue="_id"
                      value=""
                      onChange={(e) => handleAddProduct(index, e)}
                      onSearch={(kw) => fetchProducts(kw)}
                    />
                    )}
                    {inventoryEnabled && (
                      <p className="text-[11px] text-slate-400">
                        Several brands can fill this dose; the cashier picks one
                        when billing.
                        {formData.form
                          ? ` Only ${scaleLabel(formData.form)} products are listed.`
                          : " All must use the same stock unit (e.g. all Tablet)."}
                      </p>
                    )}
                  </div>
                )
              )}
            </div>
          </div>

          {/* Strength unit + Form */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">
                Strength unit
              </label>
              <select
                onChange={onchange}
                value={formData.type}
                name="type"
                className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-transparent outline-none transition-all"
              >
                <option value="">Select unit</option>
                {strengthUnits.map((u) => (
                  <option key={u.value} value={u.value}>
                    {u.name}
                  </option>
                ))}
                {formData.type &&
                  !strengthUnits.some((u) => u.value === formData.type) && (
                    <option value={formData.type}>{formData.type}</option>
                  )}
              </select>
              <p className="mt-1 text-[11px] text-slate-400">
                Printed after the dose, e.g. 500{formData.type || "mg"}
              </p>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">Form</label>
              <select
                onChange={onchange}
                value={formData.form}
                name="form"
                className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-transparent outline-none transition-all"
              >
                <option value="">Select form</option>
                {scales.map((u) => (
                  <option key={u.value} value={u.value}>
                    {u.name}
                  </option>
                ))}
              </select>
              <p className="mt-1 text-[11px] text-slate-400">
                How it is stocked. Linked products must use this unit.
              </p>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 bg-slate-50 border-t border-slate-100 flex justify-end gap-3 shrink-0">
          <button
            onClick={() => close(false)}
            className="px-5 py-2.5 text-slate-600 bg-white border border-slate-200 rounded-xl hover:bg-slate-50 transition-colors font-medium"
          >
            Cancel
          </button>
          <button
            onClick={(e) => onsubmit(e)}
            className="px-5 py-2.5 bg-gradient-to-r from-emerald-500 to-teal-600 text-white rounded-xl hover:shadow-lg hover:shadow-emerald-200 transition-all font-medium"
          >
            {id === "" ? "Submit" : "Update"}
          </button>
        </div>
      </div>
    </div>
  );
}

export default AddDrugs;
